/**
 * Этап 4 / KS-07 — read-only rendering of rich text.
 *
 * The counterpart of {@link RichTextEditor}: whatever the editor (or a rewritten AI
 * prompt) produced, this is how it appears on screen — the same markup that later
 * goes into the email, so the recruiter's preview matches what the client receives.
 *
 * This component uses `dangerouslySetInnerHTML`, which the previous Markdown-segment
 * renderer deliberately avoided. That is unavoidable for HTML content and is why
 * every value passes {@link toDisplayHtml} (DOMPurify + the shared allow-list) first.
 * The prop is intentionally NOT pre-sanitized HTML — callers pass the RAW stored
 * value and sanitizing happens here, so no call site can forget it.
 */
import type { JSX } from 'react';
import { toDisplayHtml } from './richTextSanitize.js';

export interface RichTextViewProps {
  /** RAW stored value — Markdown or HTML. Sanitized by this component. */
  readonly value: unknown;
  /** Extra classes for the wrapper (spacing/typography at the call site). */
  readonly className?: string;
  readonly testId?: string;
  /** Rendered when the value is empty, instead of a blank area. */
  readonly emptyText?: string;
}

/**
 * Prose styling for the rendered block.
 *
 * Tailwind 4 here has no typography plugin, so the element rules are spelled out:
 * without them `<ul>` loses its bullets and `<h2>` renders at body size, and the
 * preview stops matching the email — which is the whole point of the preview.
 */
const PROSE_CLASS = [
  'text-sm leading-relaxed text-[var(--color-text-strong)]',
  '[&_p]:my-2',
  '[&_h1]:mt-3 [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold',
  '[&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold',
  '[&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-0.5',
  '[&_a]:text-[var(--color-primary)] [&_a]:underline',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:text-[var(--color-text-muted)]',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-slate-50 [&_pre]:p-2 [&_pre]:text-xs',
  '[&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:text-xs',
  '[&_hr]:my-3 [&_hr]:border-slate-200',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:p-1.5 [&_th]:text-left',
  '[&_td]:border [&_td]:border-slate-200 [&_td]:p-1.5',
  '[&_img]:max-w-full',
].join(' ');

/** Render stored rich text (Markdown or HTML) as sanitized, styled HTML. */
export function RichTextView({
  value,
  className,
  testId,
  emptyText,
}: RichTextViewProps): JSX.Element | null {
  const html = toDisplayHtml(value);
  if (!html) {
    if (!emptyText) return null;
    return (
      <p
        className={`text-sm italic text-[var(--color-text-muted)] ${className ?? ''}`}
        data-testid={testId}
      >
        {emptyText}
      </p>
    );
  }
  return (
    <div
      className={`${PROSE_CLASS} ${className ?? ''}`}
      data-testid={testId}
      // Sanitized on the line above (DOMPurify + the shared allow-list); the server
      // sanitizes on ingest and again before send. See ./richTextSanitize.ts.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
